import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { ThemeColors } from '../../context/ThemeContext';
import { useDocumentFileActions } from '../../hooks/useDocuments';
import { documentsAPI } from '../../services/api';
import { ParentRequestDetail } from '../../types';
import { BorderRadius, FontSize, Spacing } from '../../theme/colors';
import {
  displayAnswer,
  extractDocumentError,
  formatDocDate,
  formatDocDateTime,
  formatFee,
  hasFee,
  formatFileSize,
  requestStatusIcon,
} from '../../utils/documents';
import { Logger } from '../../utils/logger';
import { CenteredLoader, ErrorBanner, RequestTimeline, StatusPill, useDocStyles } from './DocumentUi';
import { ResubmitPanel } from './DocumentForm';

const REASON_MAX = 500;

interface Props {
  requestId: string;
  /** Native: scrolls itself with pull-to-refresh. Web: the page shell scrolls. */
  scrollable: boolean;
  notify: (message: string, type: 'success' | 'error') => void;
}

/** Everything about one request, shared by the native and web detail screens. */
export function RequestDetailView({ requestId, scrollable, notify }: Props) {
  const { colors, s } = useDocStyles();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [detail, setDetail] = useState<ParentRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const latest = useRef(0);
  const load = useCallback(
    async (mode: 'initial' | 'refresh' | 'quiet' = 'initial') => {
      const ticket = ++latest.current;
      if (mode === 'refresh') setRefreshing(true);
      if (mode === 'initial') setLoading(true);
      setError(null);
      try {
        const { data } = await documentsAPI.getRequest(requestId);
        if (ticket === latest.current) setDetail(data);
      } catch (err) {
        if (ticket !== latest.current) return;
        Logger.error('Documents', 'Failed to load document request', err);
        setError(extractDocumentError(err, 'Could not load this request.').message);
      } finally {
        if (ticket === latest.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [requestId],
  );

  useEffect(() => {
    setDetail(null);
    load('initial');
  }, [load]);

  // Native keeps this screen mounted; refresh quietly whenever it is shown again.
  const loadRef = useRef(load);
  loadRef.current = load;
  const isFocused = useIsFocused();
  const wasFocused = useRef(isFocused);
  useEffect(() => {
    if (isFocused && !wasFocused.current) loadRef.current('quiet');
    wasFocused.current = isFocused;
  }, [isFocused]);

  const files = useDocumentFileActions(notify, () => load('quiet'));

  const confirmCancel = async () => {
    if (!detail || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const { data } = await documentsAPI.cancel(detail.id, cancelReason);
      setDetail(data);
      setCancelOpen(false);
      setCancelReason('');
      notify('Request cancelled.', 'success');
    } catch (err) {
      Logger.error('Documents', 'Failed to cancel document request', err);
      setCancelError(extractDocumentError(err, 'Could not cancel this request.').message);
    } finally {
      setCancelling(false);
    }
  };

  const renderBody = () => {
    if (loading && !detail) return <CenteredLoader message="Loading request..." />;
    if (!detail) return error ? <ErrorBanner message={error} onRetry={() => load('initial')} /> : null;

    const d = detail;
    const issuedRef = d.generatedDocumentId
      ? { id: d.generatedDocumentId, documentName: d.documentName, documentNumber: d.documentNumber }
      : null;
    const downloading = !!issuedRef && files.busyKey === `save:${issuedRef.id}`;
    const viewing = !!issuedRef && files.busyKey === `open:${issuedRef.id}`;

    return (
      <View>
        {!!error && <ErrorBanner message={error} onRetry={() => load('initial')} />}

        {/* Header */}
        <View style={s.card}>
          <View style={s.cardHeader}>
            <View style={s.cardHeaderText}>
              <Text style={styles.title} accessibilityRole="header">
                {d.documentName}
              </Text>
              <Text style={s.cardSubtitle}>
                {d.studentName}
                {d.className ? ` · ${d.className}` : ''}
              </Text>
            </View>
            <StatusPill label={d.statusLabel} tone={d.statusTone} icon={requestStatusIcon(d.status)} />
          </View>
          <View style={styles.factGrid}>
            <Fact label="Request no." value={d.requestNumber} styles={styles} />
            <Fact label="Requested" value={formatDocDate(d.requestedAt)} styles={styles} />
            {!!d.expectedBy && !d.issuedAt && d.status !== 'REJECTED' && d.status !== 'CANCELLED' && (
              <Fact label="Expected by" value={formatDocDate(d.expectedBy)} styles={styles} />
            )}
            {hasFee(d.fee) && <Fact label="Fee" value={formatFee(d.fee)} styles={styles} />}
            {!!d.documentNumber && <Fact label="Document no." value={d.documentNumber} styles={styles} />}
            {!!d.issuedAt && <Fact label="Issued" value={formatDocDate(d.issuedAt)} styles={styles} />}
          </View>
          {!!d.purpose && <Text style={s.cardBody}>Purpose: {d.purpose}</Text>}
        </View>

        {/* Ready */}
        {d.canDownload && !!issuedRef && (
          <View style={[s.card, { borderColor: colors.success }]}>
            <View style={styles.inlineRow}>
              <FeatherIcon name="check-circle" size={18} color={colors.success} />
              <Text style={styles.cardHeading}>Your document is ready</Text>
            </View>
            <Text style={s.helperText}>Download it now or any time later from My Documents.</Text>
            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[s.outlineBtn, styles.flexBtn, viewing && s.btnDisabled]}
                onPress={() => files.issuedDocument(issuedRef, 'open')}
                disabled={viewing || downloading}
                accessibilityRole="button"
                accessibilityLabel={`View ${d.documentName}`}>
                {viewing ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <FeatherIcon name="eye" size={14} color={colors.primary} />
                )}
                <Text style={s.outlineBtnText}>View</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.primaryBtn, styles.flexBtn, downloading && s.btnDisabled]}
                onPress={() => files.issuedDocument(issuedRef, 'save')}
                disabled={viewing || downloading}
                accessibilityRole="button"
                accessibilityLabel={`Download ${d.documentName}`}>
                {downloading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <FeatherIcon name="download" size={14} color="#FFFFFF" />
                )}
                <Text style={s.primaryBtnText}>Download PDF</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Action required */}
        {d.canResubmit && (
          <View style={[s.card, { borderColor: colors.warning }]}>
            <View style={styles.inlineRow}>
              <FeatherIcon name="alert-triangle" size={18} color={colors.warning} />
              <Text style={styles.cardHeading}>Action required</Text>
            </View>
            {!!d.correction?.categoryLabel && (
              <Text style={styles.reasonCategory}>{d.correction.categoryLabel}</Text>
            )}
            <View style={[s.banner, { backgroundColor: colors.warningLight, marginTop: Spacing.sm }]}>
              <Text style={s.bannerText}>
                {d.correction?.message?.trim() || 'The school needs a few changes before it can continue.'}
              </Text>
            </View>
            {!!d.correction?.requestedAt && (
              <Text style={styles.metaLine}>Asked {formatDocDateTime(d.correction.requestedAt)}</Text>
            )}
            <View style={styles.divider} />
            <ResubmitPanel
              detail={d}
              onResubmitted={updated => {
                setDetail(updated);
                notify('Changes sent to the school.', 'success');
              }}
            />
          </View>
        )}

        {/* Rejected */}
        {d.status === 'REJECTED' && (
          <View style={[s.card, styles.rejectedCard]}>
            <View style={styles.inlineRow}>
              <FeatherIcon name="x-circle" size={18} color={colors.danger} />
              <Text style={[styles.cardHeading, { color: colors.danger }]}>Request not approved</Text>
            </View>
            {!!d.rejectionCategoryLabel && <Text style={styles.reasonCategory}>{d.rejectionCategoryLabel}</Text>}
            <Text style={[s.cardBody, { color: colors.textPrimary }]}>
              {d.rejectionReason?.trim() || 'The school did not give a reason. Contact the office for details.'}
            </Text>
          </View>
        )}

        {/* Timeline */}
        {d.timeline.length > 0 && (
          <View style={s.card}>
            <Text style={s.sectionTitle}>Progress</Text>
            <RequestTimeline steps={d.timeline} />
          </View>
        )}

        {/* Answers */}
        {(d.fields.length > 0 || d.attachments.length > 0) && (
          <View style={s.card}>
            <Text style={s.sectionTitle}>Your answers</Text>
            {d.fields
              .filter(field => field.fieldType !== 'FILE')
              .map(field => (
                <View key={field.key} style={styles.answerRow}>
                  <Text style={styles.answerLabel}>{field.label}</Text>
                  <Text style={styles.answerValue}>{displayAnswer({ fieldType: field.fieldType }, field.value)}</Text>
                </View>
              ))}
            {d.attachments.length > 0 && (
              <>
                <Text style={[s.sectionTitle, { marginTop: Spacing.md }]}>Attachments</Text>
                {d.attachments.map(file => {
                  const busy = files.busyKey === `attachment:${file.id}`;
                  return (
                    <TouchableOpacity
                      key={file.id}
                      style={styles.attachmentRow}
                      onPress={() => files.attachment(d.id, file)}
                      disabled={busy}
                      accessibilityRole="button"
                      accessibilityLabel={`Open attachment ${file.fileName}`}>
                      <FeatherIcon name="paperclip" size={16} color={colors.primary} />
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.attachmentName} numberOfLines={1}>
                          {file.fileName}
                        </Text>
                        <Text style={styles.metaLine}>
                          {file.fieldLabel}
                          {file.sizeBytes ? ` · ${formatFileSize(file.sizeBytes)}` : ''}
                        </Text>
                      </View>
                      {busy ? (
                        <ActivityIndicator size="small" color={colors.primary} />
                      ) : (
                        <FeatherIcon name="external-link" size={16} color={colors.textSecondary} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </>
            )}
          </View>
        )}

        {d.canCancel && (
          <TouchableOpacity
            style={[s.dangerOutlineBtn, styles.cancelBtn]}
            onPress={() => {
              setCancelOpen(true);
              setCancelError(null);
              setCancelReason('');
            }}
            accessibilityRole="button"
            accessibilityLabel="Cancel this request">
            <FeatherIcon name="x" size={14} color={colors.danger} />
            <Text style={s.dangerOutlineBtnText}>Cancel request</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <>
      {scrollable ? (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load('refresh')}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }>
          {renderBody()}
        </ScrollView>
      ) : (
        renderBody()
      )}

      <Modal visible={cancelOpen} transparent animationType="fade" onRequestClose={() => !cancelling && setCancelOpen(false)}>
        <View style={s.modalOverlay}>
          <View style={s.dialog}>
            <Text style={s.dialogTitle}>Cancel this request?</Text>
            {!!detail && (
              <Text style={s.helperText}>
                {detail.documentName} for {detail.studentName} · {detail.requestNumber}. The school will stop working on it.
              </Text>
            )}
            <TextInput
              style={[s.input, styles.reasonInput]}
              value={cancelReason}
              onChangeText={setCancelReason}
              placeholder="Reason (optional)"
              placeholderTextColor={colors.textMuted}
              multiline
              maxLength={REASON_MAX}
              textAlignVertical="top"
              editable={!cancelling}
              accessibilityLabel="Reason for cancelling (optional)"
            />
            {!!cancelError && <ErrorBanner message={cancelError} />}
            <View style={s.dialogActions}>
              <TouchableOpacity
                style={s.bigSecondaryBtn}
                onPress={() => setCancelOpen(false)}
                disabled={cancelling}
                accessibilityRole="button">
                <Text style={s.bigSecondaryBtnText}>Keep request</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.bigDangerBtn, cancelling && s.btnDisabled]}
                onPress={confirmCancel}
                disabled={cancelling}
                accessibilityRole="button"
                accessibilityLabel="Confirm cancel request">
                {cancelling ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={s.bigPrimaryBtnText}>Cancel request</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

function Fact({ label, value, styles }: { label: string; value: string; styles: ReturnType<typeof createStyles> }) {
  return (
    <View style={styles.fact}>
      <Text style={styles.factLabel}>{label}</Text>
      <Text style={styles.factValue} selectable>
        {value}
      </Text>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    scrollContent: { padding: Spacing.md, paddingBottom: Spacing.xxl },
    title: { fontSize: FontSize.xl, fontWeight: '800', color: colors.textPrimary },
    factGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: Spacing.md, rowGap: Spacing.sm },
    fact: { width: '50%', paddingRight: Spacing.sm },
    factLabel: { fontSize: FontSize.xs, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 },
    factValue: { fontSize: FontSize.md, fontWeight: '600', color: colors.textPrimary, marginTop: 2 },
    inlineRow: { flexDirection: 'row', alignItems: 'center', gap: Spacing.sm },
    cardHeading: { fontSize: FontSize.lg, fontWeight: '800', color: colors.textPrimary, flex: 1 },
    buttonRow: { flexDirection: 'row', gap: Spacing.sm, marginTop: Spacing.md },
    flexBtn: { flex: 1 },
    reasonCategory: {
      fontSize: FontSize.xs,
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      color: colors.textSecondary,
      marginTop: Spacing.sm,
    },
    metaLine: { fontSize: FontSize.xs, color: colors.textSecondary, marginTop: 2 },
    divider: { height: 1, backgroundColor: colors.divider, marginVertical: Spacing.md },
    rejectedCard: { borderLeftWidth: 4, borderLeftColor: colors.danger },
    answerRow: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: colors.divider },
    answerLabel: { fontSize: FontSize.sm, fontWeight: '600', color: colors.textSecondary },
    answerValue: { fontSize: FontSize.md, color: colors.textPrimary, marginTop: 3, lineHeight: 20 },
    attachmentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      padding: Spacing.md,
      marginBottom: Spacing.sm,
    },
    attachmentName: { fontSize: FontSize.md, fontWeight: '600', color: colors.textPrimary },
    cancelBtn: { alignSelf: 'center', marginTop: Spacing.sm, paddingHorizontal: Spacing.lg },
    reasonInput: { minHeight: 80, marginTop: Spacing.md, marginBottom: Spacing.sm },
  });
